import useEmblaCarousel from "embla-carousel-react";
import React from "react";
import styled from "styled-components";
import type { ProductReference } from "../../types/types.PIM";
import { CardProductCompact } from "../CardProductCompact/CardProductCompact";
import { NonClickableProductCard } from "../CardProductCompact/CardProductCompact";
import { spacingTokens } from "../../designTokens";

const EmblaViewport = styled.div`
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  @media (max-width: 768px) {
    padding-left: 0;
    padding-right: 0;
  }
`;

const EmblaContainer = styled.div`
  display: flex;
  align-items: stretch;
  margin: 0 -12px;
`;

const EmblaSlide = styled.div`
  flex: 0 0 auto;
  width: 280px;
  display: flex;
  flex-direction: column;
  margin: 0 ${({ theme }) => theme.resolve("spacing_lg")};
`;

const SlideContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const EmblaHeader = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.resolve("spacing_sm")};
`;

const EmblaNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  margin-left: ${({ theme }) => theme.resolve("spacing_sm")};
  border-radius: ${({ theme }) => theme.resolve("radius_button_standard")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  background: ${({ theme }) => theme.resolve("color_neutral_white")};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    background: ${({ theme }) => theme.resolve("color_bg_surface_subtle")};
    border-color: ${({ theme }) => theme.resolve("color_border_light")};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export function ProductRefEmblaCarousel({
  products,
  onProductClick,
  dynamicPreviewBlocksByProductId,
  inaccessibleProductIds,
}: {
  products: ProductReference[];
  onProductClick: (p: ProductReference) => void;
  dynamicPreviewBlocksByProductId?: Record<
    string,
    { header: string; content: string }[]
  >;
  inaccessibleProductIds?: Set<string>;
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: "keepSnaps",
    dragFree: false,
  });
  const [canPrev, setCanPrev] = React.useState(false);
  const [canNext, setCanNext] = React.useState(false);

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanPrev(emblaApi.canScrollPrev());
      setCanNext(emblaApi.canScrollNext());
    };
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const scrollPrev = () => emblaApi?.scrollPrev();
  const scrollNext = () => emblaApi?.scrollNext();

  return (
    <div style={{ overflow: "hidden", width: "100%", maxWidth: "100%" }}>
      <EmblaHeader>
        <EmblaNavButton
          onClick={scrollPrev}
          disabled={!canPrev}
          aria-label="Previous"
        >
          ‹
        </EmblaNavButton>
        <EmblaNavButton
          onClick={scrollNext}
          disabled={!canNext}
          aria-label="Next"
        >
          ›
        </EmblaNavButton>
      </EmblaHeader>
      <EmblaViewport ref={emblaRef}>
        <EmblaContainer style={{ padding: `0 ${spacingTokens.spacing_xl}` }}>
          {products.map((product) => {
            const isInaccessible = inaccessibleProductIds?.has(product.id);
            const card = (
              <CardProductCompact
                product={product}
                variant={isInaccessible ? "tooltip" : undefined}
                onProductClick={isInaccessible ? undefined : onProductClick}
                dynamicPreviewBlocks={
                  dynamicPreviewBlocksByProductId?.[product.id]
                }
              />
            );

            return (
              <EmblaSlide key={product.id}>
                <SlideContent>
                  {isInaccessible ? (
                    <NonClickableProductCard>{card}</NonClickableProductCard>
                  ) : (
                    card
                  )}
                </SlideContent>
              </EmblaSlide>
            );
          })}
        </EmblaContainer>
      </EmblaViewport>
    </div>
  );
}
